import React, { useContext, useState, useEffect, useRef } from 'react'
import { View, StyleSheet, ScrollView, TouchableOpacity, KeyboardAvoidingView } from 'react-native'
import { Text, Button, Icon, Layout, Modal, Spinner, useTheme } from '@ui-kitten/components'
import MoneyTextInput from '../../components/MoneyTextInput'
import { UserContext } from '../../global/UserProvider'
import { getCashboxTransactionsOnDateRange, saveCashboxTransaction, deleteCashboxTransaction, fieldValue, getSalesOnDateRange, editCashboxShiftStats, cancelShiftClosing, updateStoreWiponCredentials } from '../../global/Firebase/firebase'
import { saveWorkingHours, deleteWorkingHours } from '../../global/Firebase/workingHours'
import { TRANSACTION_TYPE } from './CashboxTransactionScreen'
import { FontAwesome } from '@expo/vector-icons'
import { setNewLKTokens, getCurrentShiftStats, loadLKTokensFromCache, checkLKTokens } from '../../global/lightkassa'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import HeaderIconButton from '../../components/HeaderIconButton'
import { getActiveShift, refreshCredentials } from '../../global/wipon'
import { format } from 'date-fns'
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import { usePostHog } from 'posthog-react-native'
import { useToast } from 'react-native-toast-notifications'
import { useColors } from '../../utils/useColors'
import { validateDailyStats } from '../../utils/validationFunctions'
import { getLocalStoreDate, businessDayRangeForStore, getDailyStatID, normalizeDate } from '../../utils/timezoneFunctions'
import { parseDecimalAmount } from '../../utils/decimalInput'
import { constantsAtom, currencyFormatterAtom, dailyStatsAtom, productsDictAtom, storesDictAtom, userRoleAtom, usersDictAtom } from '../../global/AtomsProvider'
import { checkDailyStats } from '../Admin/MiscScreen'
import { useTranslation } from 'react-i18next'
import { getDefaultStore, useAtomValue } from 'jotai'

// Import for local use (employee working-hours modal) and re-export for backward compatibility.
// A bare `export { WORKING_HOURS } from '...'` does NOT create a local binding, so the JSX
// references below would throw `WORKING_HOURS is not defined` once the shift open-state view
// renders — crashing the screen. Import it locally, then re-export.
import { WORKING_HOURS } from '../../utils/workingHoursConstants'
import { isFiscalizationEnabled } from '../../utils/fiscalization'
import { SessionOutboxV2Banner } from '../../components/OutboxV2Banner'
import { cashboxAmount, computeSystemEndAmounts, getCashWallet } from './systemEndAmounts'
import { isWriteOutcomeUnknown, saveFailureText } from '../../global/Firebase/writeOutcome'
export { WORKING_HOURS }

export const countSystemEndAmounts = (dailyStats, store) => {
  let jotaiStore =  getDefaultStore()
  let constants = jotaiStore.get(constantsAtom)
  let wallet = getCashWallet(constants.paymentMethods.data, store)
  return computeSystemEndAmounts(dailyStats[store], constants.paymentMethods.data, wallet)
}

export default function ShiftScreen( { navigation, route } ) {
  const colors = useColors()
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const usersDict = useAtomValue(usersDictAtom)
  const productsDict = useAtomValue(productsDictAtom)
  const dailyStats = useAtomValue(dailyStatsAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const postHog = usePostHog()
  const toast = useToast()
  const [startCashboxAmountInputValue, setStartCashboxAmountInputValue] = useState('')
  const [employeeModalVisible, setEmployeeModalVisible] = useState(false)
  // `null` = nothing selected (the '+ add employee' flow). It used to initialise to `false`
  // and be reset to `null`, i.e. two spellings of the same state.
  const [selectedWorkingHour, setSelectedWorkingHour] = useState(null)
  const [startHour, setStartHour] = useState(null)
  const [endHour, setEndHour] = useState(null)
  const [ofdStats, setOfdStats] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [wHIsLoading, setWHIsLoading] = useState(false)
  const [store, setStore] = useState(context.user?.store?.id)
  const [prevStore, setPrevStore] = useState(context.user?.store?.id)
  const [wallet, setWallet] = useState(getCashWallet(constants?.paymentMethods?.data, context.user?.store?.id))
  const [showEditShiftModal, setShowEditShiftModal] = useState(false)
  const [editAmount, setEditAmount] = useState('')
  // ATL-1371 — the edit modal needs its OWN error state. `saveError` is shared with
  // `compareSalesToOFDTickets` (which runs while the shift is open), so rendering it inside the
  // modal would have shown the cashier a fiscal-operator mismatch directly above «Сумма», with no
  // relation to the amount — and `saveError` is not cleared on a successful save, so a refusal
  // would still be on screen the next time the modal opened.
  const [editAmountError, setEditAmountError] = useState('')
  const [editType, setEditType] = useState()
  const [editPaymentMethod, setEditPaymentMethod] = useState()
  const [cashExtractions, setCashExtractions] = useState([])
  const [cashInsertions, setCashInsertions] = useState([])
  const [saveError, setSaveError] = useState('')
  const [ofdStatsLoading, setOfdStatsLoading] = useState(false)
  // ATL-1358: ids whose delete is in flight, so the row cannot be tapped a second time while it
  // is still on screen. The data layer is idempotent as well (`runIdempotentDelete`); this half
  // is what stops the second tap from ever being issued.
  //
  // A ref, NOT state, and the distinction is the whole point: `setState` does not update the
  // value this closure already captured, so two taps landing in the same render tick would BOTH
  // read an empty set and both fire. A guard that only holds when the taps are far enough apart
  // is not a guard against double-tapping.
  const deletingCashboxIds = useRef(new Set())

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
    if (userRole?.permissions?.['Stats'] || context.user?.role === 'admin') {
      navigation.setOptions({
        headerRight: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('ShiftsStats', { store })
            }}
            iconName='bar-chart-outline'
          />
        ),
      })
    }
  }, [store])

  // ATL-2001: the shift on screen is the store's BUSINESS day (autoclose-aware) — the day
  // `dailyStats[store]` is, in both read modes (`getDailyStatID` / the Supabase feed). These reads
  // used the store's CALENDAR day (and the user's own store, not the one on screen), so between
  // midnight and the cutoff they looked at the next day: the 00:04 close, the day's deposits and
  // withdrawals were missing, and «Отменить закрытие» / editing the shift amounts found no
  // transaction and silently did nothing.
  const shiftBusinessDayRange = () => businessDayRangeForStore(store)

  useEffect(() => {
    if (!context.unsubscribeEmployeesOnShift || store !== prevStore) {
      let localStoreRange = shiftBusinessDayRange()
      var unsubscribe = db.collection('workingHours').where('store', '==', store)
      unsubscribe = unsubscribe.where('date', '>=', localStoreRange.startDate).where('date', '<=', localStoreRange.endDate).onSnapshot({ includeMetadataChanges: true }, function(querySnapshot) {
        var docs = []
        querySnapshot.forEach(function(doc) {
          var snap = doc.data()
          snap.id = doc.id
          snap.ref = doc.ref
          snap.hasPendingWrites = doc.metadata.hasPendingWrites
          docs.push(snap)
        })
        setContext(context => ({...context, employeesOnShift: docs}))
      })
      setContext(context => ({...context, unsubscribeEmployeesOnShift: unsubscribe}))
      setPrevStore(store)
    }
    getCashboxTransactions()
    setWallet(getCashWallet(constants?.paymentMethods?.data, store))
    // check if daily stat is right for sales and returns
    // if (constants?.paymentMethods) validateDailyStats(new Date(), store, true, {sales: true})
  }, [store, constants?.paymentMethods?.data?.length, storesDict?.[store]?.id])

  const getOfdStats = async () => {
    setOfdStatsLoading(true)
    let newStats
    if (storesDict?.[store]?.fiscalOperator?.id === 'light-kassa') {
      await loadLKTokensFromCache(store)
      await checkLKTokens(storesDict?.[store].LKCredentials, store)
      newStats = await getCurrentShiftStats()
      if (!newStats) {
        await setNewLKTokens(storesDict?.[store].LKCredentials, store)
        newStats = await getCurrentShiftStats()
      }
      setOfdStats(newStats)
    } else if (storesDict?.[store]?.fiscalOperator?.id === 'wipon-kassa') {
      newStats = await getActiveShift(storesDict?.[store]?.wiponCredentials)
      if (!newStats) {
        // ATL-1170: this used to build `{...credentials, accessToken: await getAccessToken(...)}`
        // and write it back unconditionally, so a failed login replaced working credentials with a
        // blob carrying no token. It also wrote through the raw Firestore ref, which skips the
        // Supabase dual-write entirely and is a no-op wherever the store carries no `ref`.
        const refreshed = await refreshCredentials(storesDict?.[store]?.wiponCredentials)
        if (refreshed) {
          try {
            await updateStoreWiponCredentials(storesDict?.[store]?.ref, refreshed)
          } catch (error) {
            Sentry.captureException(error, { tags: { books_write_error: 'store-wipon-credentials-update' } })
          }
          newStats = await getActiveShift(refreshed)
        }
      }
      compareSalesToOFDTickets(newStats)
      setOfdStats(newStats)
    }
    setOfdStatsLoading(false)
  }

  const compareSalesToOFDTickets = async (ofdStats) => {
    if (!ofdStats || !constants.paymentMethods) return
    // check when shift was opened
    var shiftOpened = ofdStats.shift.report.created_at
    var shiftOpenedDate = new Date(shiftOpened)
    console.log(shiftOpenedDate)
    // check overall numbers first
    var cashSum = 0
    var cardSum = 0
    var cashReturnSum = 0
    var cardReturnSum = 0
    for (let paymentMethod of constants.paymentMethods.data) {
      if (paymentMethod.isCash) {
        cashSum += dailyStats[store][paymentMethod.id+'SalesAmount'] ? dailyStats[store][paymentMethod.id+'SalesAmount'] : 0
        cashReturnSum += dailyStats[store][paymentMethod.id+'ReturnsAmount'] ? dailyStats[store][paymentMethod.id+'ReturnsAmount'] : 0
      } else {
        cardSum += dailyStats[store][paymentMethod.id+'SalesAmount'] ? dailyStats[store][paymentMethod.id+'SalesAmount'] : 0
        cardReturnSum += dailyStats[store][paymentMethod.id+'ReturnsAmount'] ? dailyStats[store][paymentMethod.id+'ReturnsAmount'] : 0
      }
    }
    var even = true
    if ((cashSum + cashReturnSum) != (ofdStats.shift.report.cash_sum + ofdStats.shift.report.refund_sales_cash)) {
      even = false
    }
    if ((cardSum + cardReturnSum) != (ofdStats.shift.report.card_sum + ofdStats.shift.report.refund_sales_card)) {
      even = false
    }
    if (!even) {
      console.log(t('shift.ofdAmountError'))
      // get sales from current shift
      // ATL-1477: `store` is the FOURTH argument. It used to be passed third, where it landed in
      // `flat` — truthy, so the list came back flat, but with no store filter: this store's OFD
      // shift was reconciled against every store's sales.
      const flattenedSales = await getSalesOnDateRange(context.db, {startDate: shiftOpenedDate, endDate: new Date()}, true, store)
      
      // get OFD tickets from the shift
      let ofdTickets = ofdStats.shift.tickets || []
      console.log(ofdTickets)
      
      // Check for sales without fiscal info
      let salesWithoutFiscal = flattenedSales.filter(sale => !sale.fiscalInfo && sale.paymentMethod)
      if (salesWithoutFiscal.length > 0) {
        console.log(t('shift.ofdSalesNotSentError'), salesWithoutFiscal.length)
        setSaveError(t('shift.ofdSalesNotSentError') + ': ' + salesWithoutFiscal.length)
      }
      
      // Compare total sales amounts
      let salesAmount = flattenedSales.reduce((sum, sale) => sum + (sale.finalTotalCartPrice || 0), 0)
      let ofdTicketsAmount = ofdTickets.reduce((sum, ticket) => sum + (parseFloat(ticket.sum)/100 || 0), 0)
      
      console.log('Sales total:', salesAmount, 'OFD total:', ofdTicketsAmount, 'Difference:', Math.abs(salesAmount - ofdTicketsAmount))
      console.log('Sales count:', flattenedSales.length, 'OFD tickets count:', ofdTickets.length, 'Difference:', Math.abs(flattenedSales.length - ofdTickets.length))
      
      // Match sales to OFD tickets and find unmatched items
      const matchSalesToOfdTickets = () => {
        // Track which items have been matched
        const matchedSales = new Set()
        const matchedTickets = new Set()
        
        // Match structure to store relationships
        const matches = []
        
        // First pass: Try to match sales with their fiscalInfo directly to OFD tickets
        flattenedSales.forEach(sale => {
          if (sale.fiscalInfo && sale.fiscalInfo.id) {
            const matchingTicket = ofdTickets.find(ticket => ticket.id == sale.fiscalInfo.id)
            if (matchingTicket) {
              matches.push({ sale, ticket: matchingTicket, matchType: 'direct' })
              matchedSales.add(sale.id)
              matchedTickets.add(matchingTicket.id)
            }
          }
        })
        
        // Second pass: Try to match by amount and time proximity for remaining unmatched items
        flattenedSales.forEach(sale => {
          if (matchedSales.has(sale.id)) return // Skip already matched sales
          
          // Find matching tickets by amount and time
          const potentialMatches = []
          
          for (const ticket of ofdTickets) {
            if (matchedTickets.has(ticket.id)) continue // Skip already matched tickets
            
            const ticketTime = new Date(ticket.created_at)
            const ticketSum = parseFloat(ticket.sum)/100 || 0
            
            if (!sale.saleDate) continue
            
            const saleTime = sale.saleDate.toDate()
            const timeDiffMs = Math.abs(saleTime - ticketTime)
            const timeDiffMinutes = timeDiffMs / (1000 * 60)
            
            // Match if amount is within 1 unit and time is within 5 minutes
            if (Math.abs((sale.finalTotalCartPrice || 0) - ticketSum) < 1 && timeDiffMinutes <= 5) {
              potentialMatches.push({
                ticket,
                timeDiff: timeDiffMs
              })
            }
          }
          
          // If we found potential matches, choose the closest one by time
          if (potentialMatches.length > 0) {
            potentialMatches.sort((a, b) => a.timeDiff - b.timeDiff)
            const bestMatch = potentialMatches[0]
            
            matches.push({ sale, ticket: bestMatch.ticket, matchType: 'proximity' })
            matchedSales.add(sale.id)
            matchedTickets.add(bestMatch.ticket.id)
          }
        })
        
        // Find unmatched items
        const unmatchedSales = flattenedSales.filter(sale => !matchedSales.has(sale.id))
        const unmatchedTickets = ofdTickets.filter(ticket => !matchedTickets.has(ticket.id))
        
        return {
          matches,
          unmatchedSales,
          unmatchedTickets
        }
      }
      
      // Run the matching algorithm
      const matchingResults = matchSalesToOfdTickets()
      
      // Log matching results
      console.log(`\n===== Sales to OFD Tickets Matching Results =====`)
      console.log(`Total matches found: ${matchingResults.matches.length}`)
      console.log(`  - Direct matches: ${matchingResults.matches.filter(m => m.matchType === 'direct').length}`)
      console.log(`  - Proximity matches: ${matchingResults.matches.filter(m => m.matchType === 'proximity').length}`)
      console.log(`Unmatched sales: ${matchingResults.unmatchedSales.length}`)
      console.log(`Unmatched OFD tickets: ${matchingResults.unmatchedTickets.length}`)
      
      // Log details of unmatched sales if any
      if (matchingResults.unmatchedSales.length > 0) {
        console.log(`\n===== Unmatched Sales =====`)
        matchingResults.unmatchedSales.forEach((sale, idx) => {
          const saleTime = sale.saleDate ? format(sale.saleDate.toDate(), 'dd.MM.yyyy HH:mm:ss') : 'unknown'
          console.log(`${idx+1}. Sale ID: ${sale.id}`)
          console.log(`   Amount: ${currencyFormatter.format(sale.finalTotalCartPrice || 0)}`)
          console.log(`   Time: ${saleTime}`)
          console.log(`   Has fiscalInfo: ${sale.fiscalInfo ? 'Yes' : 'No'}`)
          if (sale.paymentMethod) {
            console.log(`   Payment method: ${sale.paymentMethod.id || 'unknown'}`)
          }
        })
      }
      
      // Log details of unmatched tickets if any
      if (matchingResults.unmatchedTickets.length > 0) {
        console.log(`\n===== Unmatched OFD Tickets =====`)
        matchingResults.unmatchedTickets.forEach((ticket, idx) => {
          const ticketTime = new Date(ticket.created_at)
          const formattedTime = format(ticketTime, 'dd.MM.yyyy HH:mm:ss')
          console.log(`${idx+1}. Ticket ID: ${ticket.id}`)
          console.log(`   Amount: ${currencyFormatter.format(parseFloat(ticket.sum)/100 || 0)}`)
          console.log(`   Time: ${formattedTime}`)
          console.log(`   Type: ${ticket.type}`)
        })
      }
      
      if (Math.abs(salesAmount - ofdTicketsAmount) > 1) { // Allow 1 unit difference for rounding
        if (!salesWithoutFiscal.length && !matchingResults.unmatchedSales.length && !matchingResults.unmatchedTickets.length) {
          setSaveError(t('shift.ofdSalesNotMatchError'))
        }
        console.log(t('shift.ofdSalesNotMatchError'), 'Sales:', salesAmount, 'OFD:', ofdTicketsAmount)
      } else if (!salesWithoutFiscal.length && !matchingResults.unmatchedSales.length && !matchingResults.unmatchedTickets.length) {
        setSaveError('')
      }
    } else {
      setSaveError('')
    }
  }

  const getCashboxTransactions = async () => {
    let transactions = await getCashboxTransactionsOnDateRange(context.db, shiftBusinessDayRange(), true, store)
    let deposits = []
    let withdrawals = []
    for (let t of transactions) {
      if (t.type.id === 'deposit') {
        deposits.push(t)
      } else if (t.type.id === 'withdrawal') {
        withdrawals.push(t)
      }
    }
    setCashExtractions(withdrawals)
    setCashInsertions(deposits)
    // Returned so `deleteCashboxTransactionRow` can verify through the CONSUMER — see there.
    return transactions
  }

  const cashboxRowId = (row) => row?.ref?.id || row?.firestoreId || row?.id

  /**
   * Tell the merchant a cashbox delete did not take effect, and only then tell the machines.
   *
   * ATL-1358 review — the order is deliberate. The toast is the ONLY channel the merchant has:
   * Sentry's quota for this project has been exhausted since 2026-09-10, so `captureException`
   * currently reports to nobody. With the reporting calls first, a throw from any of them (an
   * undefined PostHog client, an internal error) would skip the toast and escape the handler as
   * the same unhandled rejection this ticket is about — a telemetry call must never be able to
   * kill the user-visible half it is supposed to accompany (ATL-1354).
   */
  const reportCashboxDeleteFailed = (item, itemId, error) => {
    // The toast first, then the machines — and BOTH guarded. `bestEffortDualWriteDelete`'s own
    // reporter (`reportDeleteWrapperThrow`) wraps its whole body for this reason: the reporting
    // path is one of the things that can be broken, and the guard must not become the new source
    // of the exception. An escape from here would be an unhandled rejection in a `void`-ed
    // handler, which is exactly the silence this ticket is about.
    try {
      toast.show(t('shift.deleteFailed'), { type: 'danger', placement: 'bottom', duration: 5000, offset: 30 })
    } catch (toastError) {
      console.log(toastError)
    }
    try {
      console.log(error)
      Sentry.captureException(error, { tags: { books_write_error: 'cashbox-transaction-delete' } })
      postHog.capture('cashbox_transaction_delete_failed', {
        transaction_id: itemId,
        transaction_type: item?.type?.id,
        error: error?.message,
      })
    } catch (reportingError) {
      console.log(reportingError)
    }
  }

  /**
   * ATL-1358 — deleting a deposit/withdrawal from the shift list.
   *
   * This used to be an inline `onPress={async () => { await deleteCashboxTransaction(...); ... }}`
   * on each of the two rows, with no catch. `deleteCashboxTransaction` threw synchronously on the
   * Supabase read shape, so the rejection had nowhere to go: the button did nothing and reported
   * nothing — no toast, no Sentry, no PostHog — which is exactly how the customer described it.
   * A failure here has to be VISIBLE, because the alternative is a merchant who believes a
   * withdrawal was removed from a shift it is still counted in.
   *
   * WHY IT ALSO CHECKS THE REFRESHED LIST. A throw is not the only way this ends in a row that
   * did not go away. `bestEffortDualWriteDelete` is best-effort BY DESIGN: when the backend call
   * fails it queues to the outbox and returns normally, so `deleteCashboxTransaction` resolves
   * having deleted the row in Firestore and NOT in Supabase. In Supabase read mode the refetch
   * then reads the store that still holds it, and the merchant sees the trash button do nothing —
   * the exact symptom of this ticket, reached down a completely different path, with the Firestore
   * stats already reversed. So success is decided by the only question that matters — IS THE ROW
   * GONE — rather than by whether a call returned. That also covers reasons nobody has enumerated
   * yet, which a check on the return value never can.
   */
  const deleteCashboxTransactionRow = async (item) => {
    const itemId = cashboxRowId(item)
    if (itemId && deletingCashboxIds.current.has(itemId)) return
    if (itemId) deletingCashboxIds.current.add(itemId)
    try {
      try {
        await deleteCashboxTransaction(context.db, item)
      } catch (error) {
        reportCashboxDeleteFailed(item, itemId, error)
        return
      }

      // The delete itself succeeded. From here a failure means something different, and must be
      // reported differently — see the two branches below.
      let remaining
      try {
        remaining = await getCashboxTransactions()
      } catch (refreshError) {
        // ATL-1358 review — only the REFRESH failed (flaky till connectivity on the GET). The
        // delete already committed, so "Could not delete, please try again" would be a false
        // statement that invites the merchant to delete a second time.
        console.log(refreshError)
        Sentry.captureException(refreshError)
        return
      }

      if (itemId && (remaining || []).some(row => cashboxRowId(row) === itemId)) {
        reportCashboxDeleteFailed(
          item,
          itemId,
          new Error(`cashbox transaction ${itemId} still listed after a delete that reported success`),
        )
      }
    } finally {
      if (itemId) deletingCashboxIds.current.delete(itemId)
    }
  }

  const openShift = async () => {
    // ATL-1371 — the cash in the drawer at open of shift, previously stored through `parseInt`.
    //
    // The refusal has to SAY something. The old guard failed silently, which was survivable while
    // `parseInt` read almost anything ('36..5' was 36 and the shift opened, with a wrong amount);
    // a strict parse turns the same keystrokes into a button that does nothing at all — no
    // spinner, no error, no shift — and a cashier cannot start trading or tell why.
    const numberStartAmount = parseDecimalAmount(startCashboxAmountInputValue)
    if (numberStartAmount === null && startCashboxAmountInputValue !== '') {
      setSaveError(t('notANumberError'))
      return
    }
    if (numberStartAmount !== null && numberStartAmount >= 0) {
      setIsLoading(true)
      try {
        await saveCashboxTransaction(db, {
          amount: numberStartAmount,
          date: new Date(),
          dailyStatID: getDailyStatID(store, new Date()),
          cashierUID: context.user?.uid,
          type: TRANSACTION_TYPE[0],
          store: store,
          wallet: wallet,
          organizationId: context.user?.organizationId
        })
        if (userRole?.salaryType === 'day') {
          await addDayEmployeeToShift()
        }
        sendFBAnalyticsEvent('open_shift')
        postHog.capture('shift_opened')
      } catch(error) {
        Sentry.captureException(error, { tags: { books_write_error: 'shift-open' } })
        console.log(error)
        setSaveError(saveFailureText(error, t, t('error')))
      }
      setIsLoading(false)
    }
  }

  const editShiftStats = async () => {
    // ATL-1371 — every rejected input must SAY something. The first version of this guard only
    // covered a non-empty unreadable value, which left the likeliest action of all — open the
    // modal, tap Save without typing — falling through to the same silent no-op it always had.
    const numberEditAmount = parseDecimalAmount(editAmount)
    if (numberEditAmount === null) {
      setEditAmountError(t('notANumberError'))
      return
    }
    if (numberEditAmount < 0) {
      // Not `amountMustBePositive`: a till counted at 0 is a real, valid amount here.
      setEditAmountError(t('amountMustNotBeNegative'))
      return
    }
    setEditAmountError('')
    setIsLoading(true)
    try {
      let cTs = await getCashboxTransactionsOnDateRange(context.db, shiftBusinessDayRange(), true, store)

      for (var cT of cTs) {
        if (editType === 0 && (cT.type.id === TRANSACTION_TYPE[0].id)) {
          // Use centralized function for edit shift stats with dual-write
          await editCashboxShiftStats(
            context.db,
            { ...cT, openId: cT.ref.id, dailyStatID: dailyStats[store]?.id },
            0, // editType 0 = open shift
            numberEditAmount,
            store,
            null,
            wallet
          )
        } else if (editType === 3 && (cT.type.id === TRANSACTION_TYPE[3].id)) {
          // Use centralized function for edit shift stats with dual-write
          await editCashboxShiftStats(
            context.db,
            { ...cT, id: cT.ref.id, endAmounts: cT.endAmounts, dailyStatID: dailyStats[store]?.id },
            3, // editType 3 = close shift
            numberEditAmount,
            store,
            editPaymentMethod,
            wallet
          )
        }
      }
    } catch(error) {
      console.log(error)
      Sentry.captureException(error, { tags: { books_write_error: 'shift-stats-edit' } })
    }
    setIsLoading(false)
    setShowEditShiftModal(false)
  }

  const handleCancelShiftClosing = async () => {
    setIsLoading(true)
    try {
      let cTs = await getCashboxTransactionsOnDateRange(context.db, shiftBusinessDayRange(), true, store)

      for (var cT of cTs) {
        if (cT.type.id === TRANSACTION_TYPE[3].id) {
          // Use centralized function to cancel shift closing with dual-write
          await cancelShiftClosing(context.db, cT, dailyStats[store].ref)
          break
        }
      }
    } catch(error) {
      // ATL-1358 review — `cancelShiftClosing` threw on the same mock ref the trash button did,
      // and this catch reported it only to a console and to a Sentry project whose quota is
      // exhausted. The argument for the button beside it applies verbatim: a shift that stays
      // closed has to say so.
      console.log(error)
      Sentry.captureException(error, { tags: { books_write_error: 'shift-close-cancel' } })
      toast.show(t('shift.deleteFailed'), { type: 'danger', placement: 'bottom', duration: 5000, offset: 30 })
    }
    setIsLoading(false)
    setShowEditShiftModal(false)
  }

  const calculateTimeDifference = (startHour, endHour) => {
    if (endHour.value > startHour.value) {
      return endHour.value - startHour.value
    } else {
      return 24 + endHour.value - startHour.value
    }
  }

  const addHourlyEmployeeToShift = async () => {
    if (startHour != null && endHour != null) {
      try {
        await saveWorkingHours(selectedWorkingHour, {
          employeeID: selectedWorkingHour ? selectedWorkingHour.employeeID : context.user?.uid,
          role: selectedWorkingHour? selectedWorkingHour.role : context.user?.role,
          roleDetails: selectedWorkingHour ? selectedWorkingHour.roleDetails : userRole,
          salaryType: 'hour',
          startHour: startHour,
          endHour: endHour,
          amount: (calculateTimeDifference(startHour, endHour) * (selectedWorkingHour ? selectedWorkingHour.roleDetails.salary : userRole?.salary)),
          date: selectedWorkingHour ? selectedWorkingHour.date.toDate() : new Date(),
          dailyStatID: getDailyStatID(selectedWorkingHour ? selectedWorkingHour.store : store, selectedWorkingHour ? selectedWorkingHour.date.toDate() : new Date()),
          store: selectedWorkingHour ? selectedWorkingHour.store : store,
        })
      } catch(error) {
        Sentry.captureException(error, { tags: { books_write_error: 'working-hours-hourly-save' } })
        // ATL-2004: the shift entry (and its salary credit) may already be saved; say so.
        if (isWriteOutcomeUnknown(error)) toast.show(t('writeOutcomeUnknown.record'), { duration: 8000, type: 'danger' })
      }
      setSelectedWorkingHour(null)
      setEmployeeModalVisible(false)
    }
  }

  const addDayEmployeeToShift = async () => {
    setWHIsLoading(true)
    var exists = false
    for (const [index, value] of context.employeesOnShift.entries()) {
      if (value.employeeID === context.user?.uid) {
        exists = true
      } 
    }
    if (!exists) {
      try {
        await saveWorkingHours(null, {
          employeeID: context.user?.uid,
          role: context.user?.role,
          roleDetails: userRole,
          salaryType: 'day',
          amount: userRole?.salary,
          date: new Date(),
          dailyStatID: getDailyStatID(store, new Date()),
          store: store,
        })
      } catch(error) {
        Sentry.captureException(error, { tags: { books_write_error: 'working-hours-day-save' } })
        // ATL-2004: the shift entry (and its salary credit) may already be saved; say so.
        if (isWriteOutcomeUnknown(error)) toast.show(t('writeOutcomeUnknown.record'), { duration: 8000, type: 'danger' })
      }
    }
    setWHIsLoading(false)
  }

  const deleteEmployeeFromShift = async () => {
    try {
      await deleteWorkingHours(selectedWorkingHour)
    } catch(error) {
      console.log(error)
      Sentry.captureException(error, { tags: { books_write_error: 'working-hours-delete' } })
    }
    setSelectedWorkingHour(null)
    setEmployeeModalVisible(false)
  }

  return (
    <ScrollView style={{flex: 1}} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
      {/* ATL-1445 P9b: «N операций не синхронизированы» — renders nothing without a session drainer. */}
      <SessionOutboxV2Banner organizationId={context.user?.organizationId} />
      { Object.keys(storesDict).length > 1 && ['admin', 'manager'].includes(context.user?.role) && (
        <View style={styles.storeChooser}>
          { Object.entries(storesDict).map(([key, value]) => (
            <Button style={styles.storeButton} key={key} appearance='outline' status={store === key ? 'primary' : 'basic'} onPress={() => {
              setPrevStore(store)
              setStore(key)
            }}>{value.name}</Button>
          ))}
        </View>
      )}
      { dailyStats[store]?.shiftClosed && <Text status='danger' style={{marginLeft: 25, marginTop: 25, fontSize: 20}}>{t('shift.shiftClosed')}</Text> }
      {!dailyStats[store]?.shiftOpened ? (
        <View style={styles.container}>
          <Text status='danger' style={{marginTop: 25}}>{t('shift.shiftClosed')}</Text>
          <Text category='h2'>{t('shift.pleaseOpenShift')}</Text>
          <Text category='label' style={styles.label}>{t('shift.startCashboxAmount')}</Text>
          <View testID="shift-start-amount">
            <MoneyTextInput
              value={startCashboxAmountInputValue}
              onChangeText={text => setStartCashboxAmountInputValue(text)}
              style={styles.priceField}
              returnKeyType='done'
            />
          </View>
          {saveError != '' && (
            <Text category='p1' status='danger' style={styles.label}>
              {saveError}
            </Text>
          )}
          {isLoading ? (
            <View style={styles.spinner}><Spinner /></View>
          ) : (
            <View testID="shift-open"><Button style={styles.submitButton} onPress={() => {openShift()}}>{t('shift.open')}</Button></View>
          )}
        </View>
      ) : (
        <View style={styles.container}>
          <Text category='label' style={styles.label}>{t('shift.employeesOnShift')}</Text>
          <ScrollView horizontal={true}>
            {context.employeesOnShift.map((item, index) => (
              <TouchableOpacity key={item+index} onPress={() => {
                if (context.user?.uid === item.employeeID || (userRole?.permissions?.['changeOthersWorkingHours'] || context.user?.role === 'admin')) {
                  setEmployeeModalVisible(true)
                  setStartHour(item.startHour)
                  setEndHour(item.endHour)
                  setSelectedWorkingHour(item)
                }
              }}>
                <View style={styles.employeeCard}>
                  <FontAwesome name="user-circle-o" size={35} color="black" />
                  <Text>{usersDict[item.employeeID]?.name}</Text>
                  <Text>{item.salaryType === 'day' ? t('shift.fullDay') : t('shift.fromTo', {from: item.startHour.text, to: item.endHour.text})}</Text>
                  { item.hasPendingWrites && (<Text>{t('shift.saving')}</Text>) }
                </View>
              </TouchableOpacity>
            ))}
            { !dailyStats[store].shiftClosed && (
              <>
               { wHIsLoading ? (
                <View style={{alignItems: 'center', height: 40, width: 40}}><Spinner /></View>
               ) : (
                <TouchableOpacity onPress={() => {
                  if (userRole?.salaryType === 'hour') {
                    setEmployeeModalVisible(true)
                    setSelectedWorkingHour(null)
                  } else if (userRole?.salaryType === 'day') {
                    addDayEmployeeToShift()
                  }
                }}>
                  <View style={styles.employeeCard}>
                    <Icon name="plus-circle-outline" height={40} width={40} fill={theme['text-basic-color']} />
                    <Text>{t('shift.add')}</Text>
                  </View>
                </TouchableOpacity>
               )}
              </>
            )}
          </ScrollView>
          <Modal backdropStyle={styles.backdrop} visible={employeeModalVisible}>
            <Layout style={styles.modalContainer}>
              <Text category='label' style={styles.label}>{t('shift.workingHours')}</Text>
              { ((!selectedWorkingHour && userRole?.salaryType === 'hour') || selectedWorkingHour?.salaryType === 'hour') && (
                <> 
                  <PlatformDependantSelect
                    data={WORKING_HOURS} 
                    selectedOption={startHour}
                    onSelect={(itemValue, itemIndex) => {
                      setStartHour(itemValue)
                    }}
                    style={{marginBottom: 10}}
                  />
                  <PlatformDependantSelect
                    data={WORKING_HOURS} 
                    selectedOption={endHour}
                    onSelect={(itemValue, itemIndex) => {
                      setEndHour(itemValue)
                    }}
                    style={{marginBottom: 10}}
                  />
                  <Button appearance='ghost' onPress={addHourlyEmployeeToShift}>{t('save')}</Button>
                </>
              )}
              <Button appearance='ghost' status='basic' onPress={() => {
                setEmployeeModalVisible(false)
                setSelectedWorkingHour(null)
              }}>{t('cancel')}</Button>
              {/* QARASAY-KASSA-265: this modal is shared by the EDIT flow (a working-hours record
                  is selected) and the '+ add employee' flow (nothing is selected yet). The delete
                  button used to render in both, so tapping it in the add flow called
                  deleteWorkingHours(null); the throw was caught, the modal closed, and the hours
                  the cashier had just picked were discarded with no message and no way to tell it
                  apart from a successful save. There is nothing to delete until something is
                  selected. */}
              { selectedWorkingHour && (
                <Button appearance='ghost' status='danger' onPress={deleteEmployeeFromShift}>{t('delete')}</Button>
              )}
            </Layout>
          </Modal>

          <Text category='label' style={styles.label}>{t('shift.shiftInfo')}</Text>

          <Layout style={styles.cashboxStatsView}>
            { dailyStats[store]['shiftOpenedTime'] && (
              <View style={styles.rowItem}>
                <Text style={styles.rowItemName}>{t('shift.shiftOpenedTime')}</Text>
                <Text style={styles.rowItemAmount}>{format(getLocalStoreDate(store, normalizeDate(dailyStats[store]['shiftOpenedTime'])), 'd-M-y HH:mm')}</Text>
              </View>
            )}
            { dailyStats[store]['shiftClosedTime'] && (
              <View style={styles.rowItem}>
                <Text style={styles.rowItemName}>{t('shift.shiftClosedTime')}</Text>
                <Text style={styles.rowItemAmount}>{format(getLocalStoreDate(store, normalizeDate(dailyStats[store]['shiftClosedTime'])), 'd-M-y HH:mm')}</Text>
              </View>
            )}
            <Text category='label' style={styles.label}>{t('shift.cashInCashbox')}</Text>
            <View style={styles.rowItem}>
              <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.rowItemName]}>
                <Text>{t('shift.startAmount')}</Text>
                {(userRole?.permissions?.['editShiftStats'] || context.user?.role === 'admin') && (
                  <TouchableOpacity onPress={() => {
                      setEditType(0)
                      // ATL-1371 — a modal that opens must not carry the LAST edit's amount or its refusal.
                      // `editAmount` is written only by the input's onChangeText, so after one successful edit
                      // the pencil on a DIFFERENT wallet/shift opened prefilled with the previous number, and
                      // "open it and tap Save" wrote that number to the wrong place with no warning.
                      setEditAmountError('')
                      setEditAmount('')
                      setShowEditShiftModal(true)
                    }} style={{marginLeft: 5, padding: 3}}
                  >
                    <Icon name='edit-outline' height={15} width={15} fill={theme['color-primary-500']} />
                  </TouchableOpacity>
                )}
              </View>
              <Modal backdropStyle={styles.backdrop} visible={showEditShiftModal}
                     onBackdropPress={() => setShowEditShiftModal(false)}>
                <Layout style={styles.modalContainer}>
                  <Text category='label' style={styles.label}>{t('shift.amount')}</Text>
                  <MoneyTextInput
                    value={editAmount}
                    onChangeText={text => setEditAmount(text)}
                    style={styles.priceField}
                    returnKeyType='done'
                  />
                  {/* ATL-1371 — this modal had no error surface at all, so a refusal was the
                      silent dead button in another costume. Its own state, not `saveError` — see
                      the note on `editAmountError`. */}
                  {editAmountError != '' && (
                    <Text category='p1' status='danger' style={styles.label}>
                      {editAmountError}
                    </Text>
                  )}
                  {isLoading ? (
                    <View style={styles.spinner}><Spinner /></View>
                  ) : (
                    <>
                      <Button style={styles.submitButton} onPress={() => {editShiftStats()}}>{t('save')}</Button>
                      <Button  status='basic' onPress={() => setShowEditShiftModal(false)}
                            style={{ marginTop: 10 }}>
                        {t('cancel')}
                      </Button>
                    </>
                  )}
                </Layout>
              </Modal>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][wallet+'StartAmount'] || 0)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('shift.sales')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][wallet+'SalesAmount'] || 0)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('shift.returns')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][wallet+'ReturnsAmount'] || 0)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('shift.withdrawals')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][wallet+'WithdrawalsAmount'] || 0)}</Text>
            </View>
            { cashExtractions.map((item, index) => (
               <View style={styles.rowItem} key={index.toString()}>
                 <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.rowItemName]}>
                  <Text>{' - ' + format(getLocalStoreDate(store, item.date.toDate()), 'HH:mm')}</Text>
                  { (userRole?.permissions?.['editShiftStats'] || context.user?.role === 'admin') && (
                    <TouchableOpacity testID={'cashbox-delete-' + (item.ref?.id || item.firestoreId || item.id)} onPress={() => { void deleteCashboxTransactionRow(item) }} style={{marginLeft: 5, padding: 3}}>
                      <Icon name='trash-outline' height={15} width={15} fill={theme['color-primary-500']} />
                    </TouchableOpacity>
                  )}
                </View>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(item.amount)}</Text>
              </View>
            ))}
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('shift.spendings')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][wallet+'SpendingsAmount'] || 0)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('shift.paidSupplies')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][wallet+'SuppliesAmount'] || 0)}</Text>
            </View>
            <View style={styles.rowItem}>
              <Text style={styles.rowItemName}>{t('shift.deposits')}</Text>
              <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][wallet+'DepositsAmount'] || 0)}</Text>
            </View>
            { cashInsertions.map((item, index) => (
               <View style={styles.rowItem} key={index.toString()}>
                 <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.rowItemName]}>
                  <Text>{' - ' + format(getLocalStoreDate(store, item.date.toDate()), 'HH:mm')}</Text>
                  { (userRole?.permissions?.['editShiftStats'] || context.user?.role === 'admin') && (
                    <TouchableOpacity testID={'cashbox-delete-' + (item.ref?.id || item.firestoreId || item.id)} onPress={() => { void deleteCashboxTransactionRow(item) }} style={{marginLeft: 5, padding: 3}}>
                      <Icon name='trash-outline' height={15} width={15} fill={theme['color-primary-500']} />
                    </TouchableOpacity>
                  )}
                </View>
                <Text style={styles.rowItemAmount}>{currencyFormatter.format(item.amount)}</Text>
              </View>
            ))}
            <View style={styles.rowItem}>
              <Text category='s1' style={styles.rowItemName}>{t('shift.total')}</Text>
              <Text category='s1' style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store].shiftClosed ? dailyStats[store][wallet + 'SystemEndAmount'] : cashboxAmount(dailyStats[store], wallet))}</Text>
            </View>
            { dailyStats[store].shiftClosed && (
              <View style={styles.rowItem}>
                <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.rowItemName]}>
                  <Text category='s1'>{t('shift.cashEndAmount')}</Text>
                  { (userRole?.permissions?.['editShiftStats'] || context.user?.role === 'admin') && (
                    <TouchableOpacity onPress={() => {
                        setEditType(3)
                        setEditPaymentMethod(wallet)
                        setEditAmountError('')
                        setEditAmount('')
                        setShowEditShiftModal(true)
                      }} style={{marginLeft: 5, padding: 3}}
                    >
                      <Icon name='edit-outline' height={15} width={15} fill={theme['color-primary-500']} />
                    </TouchableOpacity>
                  )}
                </View>
                <Text category='s1' style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][wallet + 'EndAmount'] || 0)}</Text>
              </View>
            )}
            {constants?.paymentMethods.data.map((item, index) => 
              !item.isCash && (
              <View key={item+index}>
                <Text category='label' style={styles.label}>{item.name}</Text>
                <View style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{t('shift.sales')}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][item.id+'SalesAmount'] || 0)}</Text>
                </View>
                <View style={styles.rowItem}>
                  <Text style={styles.rowItemName}>{t('shift.returns')}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][item.id+'ReturnsAmount'] || 0)}</Text>
                </View>
                { dailyStats[store].shiftClosed && (
                  <View style={styles.rowItem}>
                    <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.rowItemName]}>
                      <Text>{t('shift.cashEndAmount')}</Text>
                      { (userRole?.permissions?.['editShiftStats'] || context.user?.role === 'admin') && (
                        <TouchableOpacity onPress={() => {
                            setEditType(3)
                            setEditPaymentMethod(item.id)
                            setEditAmountError('')
                            setEditAmount('')
                            setShowEditShiftModal(true)
                          }} style={{marginLeft: 5, padding: 3}}
                        >
                          <Icon name='edit-outline' height={15} width={15} fill={theme['color-primary-500']} />
                        </TouchableOpacity>
                      )}
                    </View>
                    <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][item.id+'EndAmount'] || 0)}</Text>
                  </View>
                )}
              </View>
             )
            )}
            <Text category='label' style={styles.label}>{t('shift.salesByEmployees')}</Text>
            { context.employeesOnShift.map((item, index) => (
              dailyStats[store][item.employeeID+'SalesAmount'] > 0 && (
                <View style={styles.rowItem} key={item.employeeID+'SalesAmount'}>
                  <Text style={styles.rowItemName}>{usersDict[item.employeeID]?.name}</Text>
                  <Text style={styles.rowItemAmount}>{currencyFormatter.format(dailyStats[store][item.employeeID+'SalesAmount'] || 0)}</Text>
                </View>
              )
            ))}
            { isFiscalizationEnabled(storesDict?.[store]) && (
              ofdStatsLoading ? (
                <View style={{flex: 1, justifyContent: 'center', alignItems: 'center'}}><Spinner /></View>
              ) : (
                <TouchableOpacity onPress={getOfdStats}>
                  <Text style={{color: colors.primary, marginTop: 10}}>{t('shift.getOfdStats')}</Text>
                </TouchableOpacity>
              )
            )}
            { ofdStats && (
              <View>
                <Text category='label' style={styles.label}>{t('shift.ofdStats')}</Text>
                { ofdStats.operStats && ofdStats.operStats.map((val, idx) => (
                  <View key={val+idx}>
                    <View style={styles.rowItem}>
                      <Text style={styles.rowItemName}>{val.operationType?.name}</Text>
                      <Text style={styles.rowItemAmount}>{currencyFormatter.format(val.amount)}</Text>
                    </View>
                    { val.paymentStats.map((payVal, indx) => (
                      <View style={styles.rowItem} key={payVal+indx}>
                        <Text style={styles.rowItemName}>{' - ' + payVal.paymentType?.name}</Text>
                        <Text style={styles.rowItemAmount}>{currencyFormatter.format(payVal.amount)}</Text>
                      </View>
                    ))}
                  </View>
                ))}
                { ofdStats.shift && 
                  <View>
                    <View style={styles.rowItem}>
                      <Text style={styles.rowItemName}>{t('shift.cashSales')}</Text>
                      <Text style={styles.rowItemAmount}>{currencyFormatter.format(ofdStats.shift.report.cash_sum)}</Text>
                    </View>
                    <View style={styles.rowItem}>
                      <Text style={styles.rowItemName}>{t('shift.cardSales')}</Text>
                      <Text style={styles.rowItemAmount}>{currencyFormatter.format(ofdStats.shift.report.card_sum)}</Text>
                    </View>
                    <View style={styles.rowItem}>
                      <Text style={styles.rowItemName}>{t('shift.cashReturns')}</Text>
                      <Text style={styles.rowItemAmount}>{currencyFormatter.format(ofdStats.shift.report.refund_sales_cash)}</Text>
                    </View>
                    <View style={styles.rowItem}>
                      <Text style={styles.rowItemName}>{t('shift.cardReturns')}</Text>
                      <Text style={styles.rowItemAmount}>{currencyFormatter.format(ofdStats.shift.report.refund_sales_card)}</Text>
                    </View>
                  </View>
                }
              </View>
            )}
            
          </Layout>
          { !dailyStats[store].shiftClosed && <View>
            <View testID="shift-cash-movement">
              <Button
                accessoryLeft={(props) => (
                  <Icon name='plus-outline' {...props} />
                )}
                appearance='outline'
                status='primary'
                onPress={() => {navigation.navigate('CashboxTransaction', {close: false, store: store})}}
                style={styles.submitButton}
              >
                {t('shift.withdrawalsAndDeposits')}
              </Button>
            </View>
            <Button 
              accessoryLeft={(props) => (
                <Icon name='plus-outline' {...props} />
              )} 
              appearance='outline' 
              status='primary' 
              onPress={() => {navigation.navigate('SpendingDetail', {detail: null, isAdmin: false})}}
              style={styles.submitButton}
            >
              {t('shift.newSpending')}
            </Button>
            <View testID="shift-close"><Button style={styles.submitButton} appearance='outline' status='danger' onPress={() => {
              navigation.navigate('CashboxTransaction', {
                close: true,
                systemEndAmounts: countSystemEndAmounts(dailyStats, store),
                store: store
              })}}>
              {t('shift.closeShiftCTA')}
            </Button></View>
          </View> }
          { dailyStats[store]?.shiftClosed && (userRole?.permissions?.['editShiftStats'] || context.user?.role === 'admin') && (
            <Button style={styles.submitButton} appearance='outline' status='danger' onPress={handleCancelShiftClosing}>
              {t('shift.cancelCloseShift')}
            </Button>
          )}
          <View style={{height: 50}}></View>
        </View>
      )}
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
    paddingBottom: 25,
  }, 
  cashboxStatsView: {
    padding: 10,
    marginBottom: 10,
  },
  rowItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  }, 
  rowItemName: {
    
  }, 
  rowItemAmount: {
    textAlign: 'right',
  },
  label: {
    marginTop: 15,
    marginBottom: 5,
  },
  submitButton: {
    marginTop: 15,
  },
  modalContainer: {
    justifyContent: 'space-around',
    alignItems: 'stretch',
    width: 320,
    padding: 16,
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  employeeCard: {
    alignItems: 'center',
    marginLeft: 10,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  },
  storeChooser: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginVertical: 5,
    justifyContent: 'center'
  }, 
  storeButton: {
    marginLeft: 5,
  },
})